import React from "react";
import { Link } from "react-router-dom";
import { UncontrolledTooltip } from "reactstrap";
import Avatar from "components/common/Avatar";

export type AssigneeAvatarItem = {
  key: number | string;
  userId?: number | null;
  label: string;
  avatar?: string | null;
  employee?: {
    id: number;
    code: string;
  };
};

const groupItemClass =
  "avatar-group-item avatar-sm d-inline-flex flex-shrink-0 overflow-hidden p-0 align-middle text-decoration-none";

export default function AssigneesAvatarGroup({
  assignees,
  detailTo,
  canReadDetail,
  canAssignMore,
  onOpenAssign,
  emptyTitle,
  addTitle = "Assign user",
  maxVisible = 3,
  showAddWhenLessThan = 5,
}: {
  assignees: AssigneeAvatarItem[];
  detailTo: string;
  canReadDetail: boolean;
  canAssignMore: boolean;
  onOpenAssign?: () => void;
  emptyTitle: string;
  addTitle?: string;
  maxVisible?: number;
  showAddWhenLessThan?: number;
}) {
  const uid = React.useId().replace(/:/g, "");
  const visible = assignees.slice(0, maxVisible);
  const overflow = assignees.length - visible.length;
  const canShowAdd =
    canAssignMore &&
    typeof onOpenAssign === "function" &&
    assignees.length < showAddWhenLessThan;

  const addBtnId = `${uid}-add`;
  const addButton = canShowAdd ? (
    <>
      <button
        id={addBtnId}
        type="button"
        className={`${groupItemClass} border-0 bg-transparent lh-1`}
        aria-label={addTitle}
        onClick={() => onOpenAssign?.()}
      >
        <div className="avatar-title rounded-circle bg-light border border-dashed text-primary">
          <i
            className="ri-add-line"
            style={{ fontSize: "1rem" }}
            aria-hidden="true"
          />
        </div>
      </button>
      <UncontrolledTooltip target={addBtnId}>{addTitle}</UncontrolledTooltip>
    </>
  ) : null;

  if (assignees.length === 0) {
    if (addButton) {
      return (
        <div className="avatar-group justify-content-start">{addButton}</div>
      );
    }
    return <span className="text-muted small">No assignees</span>;
  }

  return (
    <div className="avatar-group justify-content-start">
      {visible.map((a, idx) => {
        const tipId = `${uid}-a${idx}`;
        const inner = <Avatar name={a.label} image={a.avatar ?? undefined} />;
        const el =
          a.employee?.id != null ? (
            <Link
              key={a.key}
              id={tipId}
              to={`/employees/${a.employee.id}`}
              className={groupItemClass}
            >
              {inner}
            </Link>
          ) : (
            <span key={a.key} id={tipId} className={groupItemClass}>
              {inner}
            </span>
          );
        return (
          <React.Fragment key={a.key}>
            {el}
            <UncontrolledTooltip target={tipId}>{a.label}</UncontrolledTooltip>
          </React.Fragment>
        );
      })}
      {overflow > 0 ? (
        <>
          <Link
            id={`${uid}-ov`}
            to={canReadDetail ? detailTo : "#"}
            className={groupItemClass}
            onClick={(e) => {
              if (!canReadDetail) e.preventDefault();
            }}
          >
            <div className="avatar-title rounded-circle bg-primary text-white small fw-semibold">
              +{overflow}
            </div>
          </Link>
          <UncontrolledTooltip target={`${uid}-ov`}>
            {overflow} more assignee{overflow === 1 ? "" : "s"}
          </UncontrolledTooltip>
        </>
      ) : null}
      {addButton}
    </div>
  );
}
